import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { usePostHog } from 'posthog-js/react'
import env from '@/config/env'
import { getSessionToken } from '@/config/auth'
import { trpc } from '@/config/trpc'
import { Button } from '@/components/ui/shadcn/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/shadcn/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'

// OAuth 2.1 consent page. Reached when an MCP client redirects the user's
// browser to authorize CASSI access. Validates incoming params client-side,
// shows a brand picker, then POSTs to the api server to mint an auth code
// and redirects back to the client's redirect_uri.
export function OauthAuthorizePage() {
  const [params] = useSearchParams()
  const posthog = usePostHog()
  const navigate = useNavigate()
  const clientId = params.get('client_id') ?? ''
  const redirectUri = params.get('redirect_uri') ?? ''
  const responseType = params.get('response_type') ?? ''
  const codeChallenge = params.get('code_challenge') ?? ''
  const codeChallengeMethod = params.get('code_challenge_method') ?? ''
  const scope = params.get('scope') ?? 'brand'
  const state = params.get('state') ?? ''

  const paramError = (() => {
    if (!clientId) return 'Missing client_id'
    if (!redirectUri) return 'Missing redirect_uri'
    if (responseType !== 'code') return 'response_type must be "code"'
    if (!codeChallenge) return 'Missing code_challenge (PKCE required)'
    if (codeChallengeMethod !== 'S256') return 'code_challenge_method must be "S256"'
    return null
  })()

  const { data: brands } = trpc.brand.list.useQuery(undefined, { enabled: !paramError })
  const { data: currentBrand } = trpc.brand.getCurrent.useQuery(undefined, {
    enabled: !paramError,
  })
  const { data: subscription, isPending: subscriptionPending } = trpc.userSubscription.get.useQuery(
    undefined,
    {
      enabled: !paramError,
    },
  )
  // Without MCP on their plan the minted token would be rejected on every
  // request, which the client shows as a broken connection. Ask for the
  // upgrade here instead, where we can explain it.
  const blockedByPlan = subscription !== undefined && !subscription.hasMcpAccess

  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null)
  const [client, setClient] = useState<{ name: string; redirectUris: string[] } | null>(null)
  const [clientError, setClientError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (paramError || !clientId) return
    ;(async () => {
      try {
        const res = await fetch(`${env.VITE_API_URL}/oauth/clients/${clientId}`)
        if (!res.ok) {
          setClientError('Unknown OAuth client')
          return
        }
        const data: { name: string; redirectUris: string[] } = await res.json()
        if (!data.redirectUris.includes(redirectUri)) {
          setClientError('redirect_uri is not registered for this client')
          return
        }
        setClient(data)
      } catch (e) {
        setClientError(e instanceof Error ? e.message : 'Failed to load client')
      }
    })()
  }, [clientId, paramError, redirectUri])

  useEffect(() => {
    if (!blockedByPlan || !client) return
    posthog.capture('upgrade_prompt_shown', { limitType: 'mcp', surface: 'mcp_consent' })
  }, [blockedByPlan, client, posthog])

  useEffect(() => {
    if (selectedBrandId === null && currentBrand) setSelectedBrandId(currentBrand.id)
  }, [currentBrand, selectedBrandId])

  const buildRedirect = (extraParams: Record<string, string>) => {
    const url = new URL(redirectUri)
    Object.entries(extraParams).forEach(([k, v]) => url.searchParams.set(k, v))
    if (state) url.searchParams.set('state', state)
    return url.toString()
  }

  const handleAllow = async () => {
    if (selectedBrandId === null) return
    setSubmitError(null)
    setSubmitting(true)
    try {
      const res = await fetch(`${env.VITE_API_URL}/oauth/authorize/grant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getSessionToken() ?? ''}`,
        },
        body: JSON.stringify({
          client_id: clientId,
          redirect_uri: redirectUri,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod,
          scope,
          brand_id: selectedBrandId,
        }),
      })
      const data = await res.json()
      if (!res.ok || !data.code) {
        setSubmitError(data?.error_description ?? data?.error ?? 'Failed to grant access')
        setSubmitting(false)
        return
      }
      window.location.href = buildRedirect({ code: data.code })
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Unexpected error')
      setSubmitting(false)
    }
  }

  const handleDeny = () => {
    // Beacon, because the redirect below would drop a batched event.
    posthog.capture(
      'mcp_consent_denied',
      { client_name: client?.name },
      { send_instantly: true, transport: 'sendBeacon' },
    )
    window.location.href = buildRedirect({
      error: 'access_denied',
      error_description: 'User denied the request',
    })
  }

  const handleSeePlans = () => {
    posthog.capture('upgrade_prompt_accepted', { limitType: 'mcp', surface: 'mcp_consent' })
    navigate('/pricing')
  }

  const handleCancelUpgrade = () => {
    posthog.capture(
      'upgrade_prompt_dismissed',
      { limitType: 'mcp', surface: 'mcp_consent', action: 'cancel' },
      { send_instantly: true, transport: 'sendBeacon' },
    )
    window.location.href = buildRedirect({
      error: 'access_denied',
      error_description: 'MCP access requires a paid CASSI plan',
    })
  }

  if (paramError) return <ConsentError title="Invalid request" message={paramError} />
  if (clientError) return <ConsentError title="Cannot authorize" message={clientError} />
  // If the plan check errors, fall through: the grant endpoint enforces it too.
  if (!client || subscriptionPending) return <ConsentLoading />

  if (blockedByPlan) {
    return (
      <section className="mx-auto mt-[6vh] max-w-md p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Connecting {client.name} needs a paid plan</CardTitle>
            <CardDescription>
              AI agents like {client.name} can work inside CASSI on every paid plan. Your account is
              on the free plan, so this connection would be refused. Once you upgrade, connect again
              from {client.name}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancelUpgrade}>
                Cancel
              </Button>
              <Button onClick={handleSeePlans}>See plans</Button>
            </div>
          </CardContent>
        </Card>
      </section>
    )
  }

  const selectedBrand = brands?.find((b) => b.id === selectedBrandId)

  return (
    <section className="mx-auto mt-[6vh] max-w-md p-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Authorize {client.name}</CardTitle>
          <CardDescription>
            {client.name} wants access to your CASSI account. The connection will be limited to a
            single brand you choose below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <div className="text-sm font-medium">Brand</div>
            <Select
              value={selectedBrandId !== null ? String(selectedBrandId) : ''}
              onValueChange={(v) => setSelectedBrandId(Number(v))}>
              <SelectTrigger>
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent>
                {brands?.map((brand) => (
                  <SelectItem key={brand.id} value={String(brand.id)}>
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedBrand && (
              <p className="text-muted-foreground text-xs">
                {client.name} will be able to read and modify {selectedBrand.name}'s moodboards,
                workspaces, and scheduled posts. It cannot access your other brands.
              </p>
            )}
          </div>

          {submitError && (
            <div className="rounded-md border border-red-500/40 bg-red-500/5 p-3 text-sm text-red-500">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleDeny} disabled={submitting}>
              Deny
            </Button>
            <Button onClick={handleAllow} disabled={submitting || selectedBrandId === null}>
              {submitting ? 'Authorizing…' : 'Allow'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  )
}

const ConsentError = ({ title, message }: { title: string; message: string }) => (
  <section className="mx-auto mt-[6vh] max-w-md p-6">
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{title}</CardTitle>
        <CardDescription>{message}</CardDescription>
      </CardHeader>
    </Card>
  </section>
)

const ConsentLoading = () => (
  <section className="mx-auto mt-[6vh] max-w-md p-6">
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Loading…</CardTitle>
      </CardHeader>
    </Card>
  </section>
)
